import apiFetch from './apiFetch';

/**
 * @typedef {Object} TenantManagementRow
 * @property {number} id
 * @property {string} company
 * @property {string | null} clientId
 * @property {boolean} hasClientSecret
 * @property {boolean} fortnoxConfigured
 * @property {boolean} crmEnabled
 * @property {Record<string, boolean>} modules
 * @property {string | null} createdAt
 * @property {string | null} updatedAt
 * @property {{ users: number }} usage
 * @property {TenantDeletionDependencies} dependencies
 * @property {boolean} deletable
 */

/**
 * @typedef {Object} TenantWriteRequest
 * @property {string} company
 * @property {string | null} [clientId]
 * @property {string | null} [clientSecret]
 * @property {boolean | null} [crmEnabled]
 */

/**
 * @typedef {Object} TenantDeletionDependencies
 * @property {number} users
 * @property {number} fortnoxTokens
 * @property {number} crmRows
 * @property {number} alerts
 * @property {number} snapshots
 */

/**
 * @typedef {Object} TenantDeletionConflictPayload
 * @property {string} error
 * @property {TenantDeletionDependencies} dependencies
 */

const TENANT_WRITE_FIELDS = ['company', 'clientId', 'clientSecret', 'crmEnabled'];

function buildRequestError(defaultMessage, status, data) {
  const error = new Error(data?.error || defaultMessage);
  error.status = status;
  error.data = data;
  return error;
}

async function parseResponseData(response) {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();
  return text ? { error: text } : null;
}

function normalizeModules(modules) {
  if (!modules || typeof modules !== 'object' || Array.isArray(modules)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(modules).map(([key, enabled]) => [key, Boolean(enabled)])
  );
}

function normalizeDeletionDependencies(dependencies) {
  const source = dependencies && typeof dependencies === 'object' ? dependencies : {};
  return {
    users: Number(source.users ?? 0),
    fortnoxTokens: Number(source.fortnoxTokens ?? 0),
    crmRows: Number(source.crmRows ?? 0),
    alerts: Number(source.alerts ?? 0),
    snapshots: Number(source.snapshots ?? 0),
  };
}

/**
 * @param {unknown} data
 * @returns {TenantManagementRow}
 */
function normalizeTenant(data) {
  const tenant = data && typeof data === 'object' ? data : {};
  const usage = tenant.usage && typeof tenant.usage === 'object' ? tenant.usage : {};

  return {
    id: tenant.id,
    company: tenant.company,
    clientId: tenant.clientId ?? null,
    hasClientSecret: Boolean(tenant.hasClientSecret),
    fortnoxConfigured: Boolean(tenant.fortnoxConfigured),
    crmEnabled: Boolean(tenant.crmEnabled),
    modules: normalizeModules(tenant.modules),
    createdAt: tenant.createdAt ?? null,
    updatedAt: tenant.updatedAt ?? null,
    usage: { users: Number(usage.users ?? 0) },
    dependencies: normalizeDeletionDependencies(tenant.dependencies),
    deletable: Boolean(tenant.deletable),
  };
}

/**
 * @param {TenantWriteRequest} payload
 * @returns {Partial<TenantWriteRequest>}
 */
function serializeTenantWriteRequest(payload) {
  const request = {};

  TENANT_WRITE_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(payload || {}, field)) {
      request[field] = payload[field];
    }
  });

  return request;
}

/**
 * @returns {Promise<TenantManagementRow[]>}
 */
export async function fetchTenants() {
  const response = await apiFetch('/api/tenants');
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to load tenants.', response.status, data);
  }

  return Array.isArray(data) ? data.map(normalizeTenant) : [];
}

/**
 * @param {number} tenantId
 * @returns {Promise<TenantManagementRow>}
 */
export async function fetchTenantById(tenantId) {
  const response = await apiFetch(`/api/tenants/${tenantId}`);
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to load the tenant.', response.status, data);
  }

  return normalizeTenant(data);
}

/**
 * @param {TenantWriteRequest} payload
 * @returns {Promise<TenantManagementRow>}
 */
export async function createTenant(payload) {
  const response = await apiFetch('/api/tenants', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(serializeTenantWriteRequest(payload)),
  });
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to create the tenant.', response.status, data);
  }

  return normalizeTenant(data);
}

/**
 * @param {number} tenantId
 * @param {TenantWriteRequest} payload
 * @returns {Promise<TenantManagementRow>}
 */
export async function updateTenant(tenantId, payload) {
  const response = await apiFetch(`/api/tenants/${tenantId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(serializeTenantWriteRequest(payload)),
  });
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to update the tenant.', response.status, data);
  }

  return normalizeTenant(data);
}

/**
 * @param {number} tenantId
 * @returns {Promise<void>}
 */
export async function deleteTenant(tenantId) {
  const response = await apiFetch(`/api/tenants/${tenantId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const data = await parseResponseData(response);
    throw buildRequestError('Failed to delete the tenant.', response.status, data);
  }
}
